import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";
import {
  GetSubscriptionPlansDataTypes,
  GetSubscriptionUserPlanDataTypes,
} from "../../../../Schema/getSubscriptionPlans.types";
import { setDiscountCode } from "../../discountCode/discountCode";
import { call } from "@redux-saga/core/effects";
import { queryClient } from "../../../store";

export type GetSubscriptionPlansLoadAction = {
  type: string;
  payload: { discount_code?: string };
};

export type GetSubscriptionPlansData = {
  data: {
    plans: GetSubscriptionPlansDataTypes[];
    user_plan: GetSubscriptionUserPlanDataTypes;
    wallet_balance: string | null;
  };
};

const GetSubscriptionPlans = new ReduxFetchState<
  GetSubscriptionPlansData,
  { discount_code?: string },
  any
>("getSubscriptionPlans");

export function* watchGetSubscriptionPlans(action: GetSubscriptionPlansLoadAction) {
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();
    const { discount_code } = action.payload;

    const data: GetSubscriptionPlansData = yield call([queryClient, queryClient.fetchQuery], {
      queryKey: ["getSubscriptionPlans", lang],
      queryFn: async () => {
        const res = await axios({
          url: `/api/v1/ai/subscription/plans${discount_code ? "?discount_code=" + discount_code : ""}`,
          method: "GET",
          headers: { lang },
        });
        return { data: res.data };
      },
      staleTime: 1440 * 60 * 1000,
    });

    if (discount_code) {
      yield put(setDiscountCode({ code: discount_code, type: "subscription" }));
    }

    yield put(GetSubscriptionPlans.actions.loadSuccess({ data: data.data }));
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? "مشکلی پیش آمده",
      type: "error",
    });
    yield put(GetSubscriptionPlans.actions.loadFailure(e.message));
  }
}

export function* getSubscriptionPlansSagas() {
  yield takeEvery(GetSubscriptionPlans.actionTypes.load, watchGetSubscriptionPlans);
}

export function useGetSubscriptionPlans() {
  const { loading, data, ...getSubscriptionPlansState } = useAppSelector(
    (state) => state.getSubscriptionPlans,
  );
  const dispatch = useAppDispatch();

  const dispatchGetSubscriptionPlans = useCallback(
    (data: { discount_code?: string; isSilent?: boolean }) => {
      dispatch(GetSubscriptionPlans.actions.load(data, data.isSilent));
    },
    [dispatch],
  );

  return {
    ...getSubscriptionPlansState,
    dispatchGetSubscriptionPlans,
    getSubscriptionPlansLoading: loading,
    getSubscriptionPlansData: data,
  };
}

export const {
  actionTypes: getSubscriptionPlansActionsTypes,
  actions: getSubscriptionPlansActions,
  reducer: getSubscriptionPlansReducer,
} = GetSubscriptionPlans;
